<template>
  <div
    :class="['atom-tag', { 'atom-tag--selected': selected }, { 'atom-tag--editable': editable }, { 'atom-tag--removable': removable }]"
    :contenteditable="editable"
    :data-placeholder="placeholder"
    :role="editable ? undefined : 'checkbox'"
    :aria-checked="editable ? undefined : selected"
    :tabindex="editable ? undefined : 0"
    @click="handleClick"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @keydown.enter="handleEnter"
    @input="handleInput">
    <span v-if="!editable" class="atom-tag__text">{{ displayText }}</span>
    <div
      v-if="removable && !editable"
      class="atom-tag__remove-container"
      role="button"
      tabindex="0"
      :aria-label="`Eliminar ${displayText}`"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
      @click.stop="handleRemove"
      @keydown.enter.prevent.stop="handleRemove"
      @keydown.space.prevent.stop="handleRemove">
      <iconic name="close" class="atom-tag__remove" />
      <ToolTip v-if="showRemoveTooltip" text="Eliminar motivo" variant="down_upper" />
    </div>
  </div>
</template>

<script>
  import ToolTip from "./ToolTip.vue";

  export default {
    name: "AtomTag",
    components: {
      ToolTip,
    },
    props: {
      text: {
        type: String,
        default: "",
      },
      selected: {
        type: Boolean,
        default: false,
      },
      removable: {
        type: Boolean,
        default: false,
      },
      editable: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: "Otro",
      },
    },
    emits: ["click", "remove", "blur", "enter", "input", "tag-created"],
    data() {
      return {
        showRemoveTooltip: false,
        isEditing: false,
        internalText: "",
      };
    },
    computed: {
      displayText() {
        if (!this.editable) {
          const text = this.text || this.placeholder;
          return text.length > 20 ? text.substring(0, 20) + "..." : text;
        }
        return "";
      },
      hasRealText() {
        return this.text && this.text.trim() !== "";
      },
    },
    mounted() {
      if (this.editable) {
        this.$el.innerHTML = "";
        this.$el.textContent = "";
      }
    },
    methods: {
      placeCaretAtEnd(el) {
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      },

      handleClick() {
        if (this.editable) {
          this.isEditing = true;
          this.internalText = "";
          this.$nextTick(() => {
            this.$el.focus();
            this.$el.innerHTML = "";
            this.$el.textContent = "";
          });
        }
        this.$emit("click");
      },
      handleKeydown(event) {
        if (!this.editable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          this.handleClick();
        }
      },
      handleRemove() {
        this.$emit("remove");
      },
      handleBlur(event) {
        if (this.editable) {
          this.isEditing = false;
          let text = event.target.textContent.trim();

          if (text.length > 20) {
            const truncated = text.substring(0, 20);
            event.target.textContent = truncated;
            text = truncated;
          }

          this.internalText = text;

          if (!text) {
            this.$nextTick(() => {
              event.target.innerHTML = "";
              event.target.textContent = "";
            });
          }
        }
        this.$emit("blur", event);
      },
      handleEnter(event) {
        event.preventDefault();
        if (this.editable) {
          let text = event.target.textContent.trim();
          if (text.length > 20) {
            text = text.substring(0, 20);
            event.target.textContent = text;
          }
          if (text && text !== this.placeholder) {
            this.$emit("tag-created", text);
            this.$emit("enter", event);
            this.resetTag();
          }
        } else {
          this.$emit("enter", event);
        }
      },
      handleInput(event) {
        const max = 20;
        let text = event.target.textContent || "";
        if (text.length > max) {
          const truncatedText = text.substring(0, max);
          event.target.textContent = truncatedText;
          this.internalText = truncatedText;
          this.$emit("input", truncatedText);
          this.$nextTick(() => {
            this.placeCaretAtEnd(event.target);
          });
        } else {
          this.internalText = text;
          this.$emit("input", text);
        }
      },
      resetTag() {
        this.isEditing = false;
        this.internalText = "";
        this.$nextTick(() => {
          this.$el.innerHTML = "";
          this.$el.textContent = "";
        });
      },
      reset() {
        this.resetTag();
      },
      showTooltip() {
        this.showRemoveTooltip = true;
      },
      hideTooltip() {
        this.showRemoveTooltip = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .atom-tag {
    @include Flex();
    position: relative;
    width: max-content;
    gap: 5px;
    padding: 5px 18px;
    font-size: 14px;
    border-radius: 50px;
    color: #333;
    background: #f0f0f0;
    outline: none;
    cursor: pointer;
    transition: 0.2s ease-in-out;

    &:hover:not(&--selected) {
      background: rgba(189, 9, 9, 0.15);
      color: $primary-color;

      .atom-tag__remove-container {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }
    }

    &--removable:hover:not(&--selected) .atom-tag__text {
      transform: translateX(-8px);
    }

    &--selected {
      color: $primary-color;
      background: rgba(189, 9, 9, 0.15);

      .atom-tag__remove-container {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }

      .atom-tag__text {
        font-family: $newFont_medium;
      }
    }

    &--removable {
      &.atom-tag--selected .atom-tag__text {
        transform: translateX(-8px);
      }
    }

    &--editable {
      outline: none;
      font-family: $newFont_regular;
      &:focus,
      &:focus-visible {
        border: 1px solid #bd0909;
      }
    }

    &:focus-visible,
    .atom-tag__remove-container:focus-visible {
      outline: 2px solid $primary-color;
      outline-offset: 2px;
    }

    &:empty:before {
      content: attr(data-placeholder);
      color: #999;
      font-family: $newFont_regular;
      font-size: 15px;
      pointer-events: none;
    }

    &__text {
      display: inline-block;
      transition: transform 0.18s ease;
      white-space: nowrap;
      font-size: 14px;
      font-family: $newFont_regular;
      font-weight: 400;
      flex: 1;
      text-transform: lowercase;
      &::first-letter {
        text-transform: uppercase;
      }
    }

    &__remove-container {
      @include Flex();
      position: absolute;
      right: 8px;
      top: 52%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      border-radius: 50%;
      cursor: pointer;
      transition:
        opacity 0.18s ease,
        visibility 0.18s ease;
      overflow: visible;
      z-index: 10;
    }

    &__remove {
      font-size: 10px;
      color: #000000;
      border-radius: 50%;
      width: 16px;
      height: 16px;

      &:hover {
        color: $primary-color;
        background: #fff;
      }
    }
  }
</style>
